/**
 * /me/payment-hub — Employee self-service Payment Hub (M3X.LOANS PR4 / F.2).
 *
 * 4-tab landing page modelled after the PeopleHub Payment Hub:
 *   - Loan Request — active loan balances + recent requests + "Request A Loan"
 *   - Expense Claim Request — placeholder (future module)
 *   - Payslip — link out to /me/payslips (existing surface; placeholder here)
 *   - Guarantor Assign to Me — populated by PR20 (Guarantor system)
 *
 * Per pack §1.21 + §4.1. Tabs are deep-linkable via ?tab=loans / ?tab=expense
 * / ?tab=payslip / ?tab=guarantor.
 *
 * For PR4: the Loan Request tab is fully wired. The other three render
 * useful-coming-soon shells (per the M27 PR4 primitive pattern) so they're
 * not broken placeholders — each names what's coming + when (PR20 for
 * Guarantor, deferred for the rest).
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { GuarantorAssignedToMePanel } from "../components/GuarantorAssignedToMePanel";
import { LoansSelfServicePanel } from "../components/LoansSelfServicePanel";
import { PageHeader } from "../components/ui/PageHeader";

type Tab = "loans" | "expense" | "payslip" | "guarantor";

const TABS: { id: Tab; labelKey: string; defaultLabel: string }[] = [
  {
    id: "loans",
    labelKey: "payment_hub.tab_loans",
    defaultLabel: "Loan Request",
  },
  {
    id: "expense",
    labelKey: "payment_hub.tab_expense",
    defaultLabel: "Expense Claim Request",
  },
  {
    id: "payslip",
    labelKey: "payment_hub.tab_payslip",
    defaultLabel: "Payslip",
  },
  {
    id: "guarantor",
    labelKey: "payment_hub.tab_guarantor",
    defaultLabel: "Guarantor Assign to Me",
  },
];

export function MyPaymentHubPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useSearchParams();
  const initial =
    (search.get("tab") as Tab | null) ?? ("loans" as Tab);
  const [tab, setTab] = useState<Tab>(initial);

  const handleSelect = (next: Tab) => {
    setTab(next);
    setSearch({ tab: next }, { replace: true });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("payment_hub.page_title", "Payment Hub")}
        subtitle={t(
          "payment_hub.page_subtitle",
          "Your loans, expense claims, payslips, and guarantor liabilities — all in one place.",
        )}
      />

      <div
        role="tablist"
        aria-label={t("payment_hub.tabs_label", "Payment Hub sections")}
        className="border-b border-slate-200 flex gap-1 overflow-x-auto"
      >
        {TABS.map((entry) => (
          <button
            key={entry.id}
            role="tab"
            aria-selected={tab === entry.id}
            onClick={() => handleSelect(entry.id)}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
              tab === entry.id
                ? "border-brand-blue text-brand-deep"
                : "border-transparent text-text-secondary hover:text-text-primary"
            }`}
          >
            {t(entry.labelKey, entry.defaultLabel)}
          </button>
        ))}
      </div>

      <div role="tabpanel" aria-labelledby={`tab-${tab}`}>
        {tab === "loans" ? <LoansSelfServicePanel /> : null}
        {tab === "expense" ? (
          <ComingSoonPanel
            title={t(
              "payment_hub.expense_coming_title",
              "Expense Claim Request",
            )}
            body={t(
              "payment_hub.expense_coming_body",
              "Submit expense reimbursement claims with receipts. Coming in the Expense Management module.",
            )}
          />
        ) : null}
        {tab === "payslip" ? (
          <ComingSoonPanel
            title={t("payment_hub.payslip_coming_title", "Payslip")}
            body={t(
              "payment_hub.payslip_coming_body",
              "Your monthly payslips with full breakdown. Visit /me/payslips on the existing payroll surface (Rawatib's domain) until this tab is wired here.",
            )}
          />
        ) : null}
        {tab === "guarantor" ? <GuarantorAssignedToMePanel /> : null}
      </div>
    </div>
  );
}

function ComingSoonPanel({
  title,
  body,
}: {
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-surface-default p-8 text-center">
      <h2 className="text-lg font-semibold text-text-primary mb-2">
        {title}
      </h2>
      <p className="text-sm text-text-secondary max-w-prose mx-auto">
        {body}
      </p>
    </div>
  );
}
